import { t } from '@i18n';
import { FormHelperText, Grid, InputAdornment } from '@mui/material';
import { custom_field_entity_enum } from '@predium/enums';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import { memo, useCallback, useEffect } from 'react';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import { useBuildingFilters } from '@/components/building-filters/BuildingFiltersProvider';
import { findCustomFieldFilter, upsertCustomFieldFilter } from '@/components/building-filters/custom-field-filters';
import Collapsible from '@/components/common/Collapsible';
import NumberField from '@/components/form/NumberField';

const validateMinMax = (min: number | null, max: number | null): string | undefined =>
  !isNil(min) && !isNil(max) && min > max ? t('Validation_NumberMinBiggerThanMax') : undefined;

type Props = {
  entity: custom_field_entity_enum;
  fieldKey: string;
  title: string;
  suffix?: string | null;
};

function NumberFilter({ entity, fieldKey, title, suffix }: Props) {
  const { filters, setFilters, setFieldValidationError } = useBuildingFilters();
  const validationKey = `custom-field-${entity}-${fieldKey}`;

  const filter = findCustomFieldFilter(filters.customFields, entity, fieldKey);
  const min = filter?.min ?? null;
  const max = filter?.max ?? null;

  const error = validateMinMax(min, max);

  useEffect(() => {
    setFieldValidationError(validationKey, Boolean(error));
    return () => setFieldValidationError(validationKey, false);
  }, [error, setFieldValidationError, validationKey]);

  const handleChange = useCallback(
    (bound: 'min' | 'max', value: number | null) => {
      void setFilters((prev) => {
        const current = findCustomFieldFilter(prev.customFields, entity, fieldKey);
        return {
          ...prev,
          customFields: upsertCustomFieldFilter(prev.customFields, entity, fieldKey, {
            min: bound === 'min' ? value : (current?.min ?? null),
            max: bound === 'max' ? value : (current?.max ?? null),
          }),
        };
      });
    },
    [entity, fieldKey, setFilters],
  );

  const endAdornment = suffix ? (
    <InputAdornment position="end">
      <PTypography variant="caption" color="secondary">
        {suffix}
      </PTypography>
    </InputAdornment>
  ) : null;

  return (
    <Collapsible title={title}>
      <Grid item container alignItems="center" justifyContent="space-between">
        <Grid item xs>
          <NumberField
            label={t('General_Min')}
            value={min}
            onValueChange={(values) => handleChange('min', values.floatValue ?? null)}
            variant="outlined"
            size="small"
            isError={Boolean(error)}
            InputProps={{ endAdornment }}
          />
        </Grid>
        <Grid item>
          <PTypography variant="body1" sx={{ mx: 1.5 }}>
            -
          </PTypography>
        </Grid>
        <Grid item xs>
          <NumberField
            label={t('General_Max')}
            value={max}
            onValueChange={(values) => handleChange('max', values.floatValue ?? null)}
            variant="outlined"
            size="small"
            isError={Boolean(error)}
            InputProps={{ endAdornment }}
          />
        </Grid>
      </Grid>
      <FormHelperText error>{error}</FormHelperText>
    </Collapsible>
  );
}

export const CustomFieldNumberFilter = memo(NumberFilter, isEqual);
